import type { EngagementRequirement } from '@/lib/api/engagement-requirements';
import {
  addedFromDiscovery,
  citationOf,
} from '@/components/engagement-requirements/requirements-logic';

/**
 * The requirement counts behind the engagement Overview (its hero line,
 * the Selection card and the stage track). The only counting here is over
 * the requirement rows the Selection screen already holds in full; the
 * Discovery numbers are the server's KPIs and never pass through here.
 */

export interface RequirementSourceCounts {
  /** Active requirements (removed ones are not counted anywhere). */
  total: number;
  library: number;
  imported: number;
  manual: number;
  discovery: number;
  critical: number;
  desired: number;
  notSet: number;
}

/**
 * Active requirements by where each came from. A requirement added from a
 * discovery suggestion counts as Discovery whether it landed as a library
 * match or as a client-specific need, so the four buckets sum to the total.
 */
export function requirementSourceCounts(
  rows: ReadonlyArray<
    Pick<EngagementRequirement, 'citation' | 'number' | 'load' | 'status' | 'criticality'>
  >,
): RequirementSourceCounts {
  const out: RequirementSourceCounts = {
    total: 0,
    library: 0,
    imported: 0,
    manual: 0,
    discovery: 0,
    critical: 0,
    desired: 0,
    notSet: 0,
  };
  for (const row of rows) {
    if (row.status === 'removed') continue;
    out.total += 1;
    if (row.criticality === 'critical') out.critical += 1;
    if (row.criticality === 'desired') out.desired += 1;
    if (row.criticality === 'not_set') out.notSet += 1;
    if (addedFromDiscovery(row)) {
      out.discovery += 1;
      continue;
    }
    const kind = citationOf(row).kind;
    if (kind === 'library') out.library += 1;
    else if (kind === 'spreadsheet') out.imported += 1;
    else if (kind === 'manual') out.manual += 1;
  }
  return out;
}
